import React, { useRef, useState, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { backgroundColors } from "./utils/backgroundColors";

// Card image mapping for tooltips
const TOOLTIP_IMAGES = {
  "Visa": "/images/visa24x24.svg",
  "Mastercard": "/images/master24x24.svg",
  "Amex": "/images/amex24x24.svg",
  "Discover": "/images/discover24x24.svg",
  "Debit": "/images/debit24x24.svg",
  "EBT": "/images/ebt24x24.svg",
  "MC": "/images/master24x24.svg",
  "VS": "/images/visa24x24.svg",
  "VD": "/images/visa24x24.svg",
  "DS": "/images/discover24x24.svg",
  "MD": "/images/debit24x24.svg",
  "AM": "/images/amex24x24.svg",
  "VB": "/images/visa24x24.svg",
  "DD":  "/images/ebt24x24.svg",
  "MB": "/images/master24x24.svg",
};

ChartJS.register(ArcElement, Tooltip, Legend);

const DoubleDonutChart = ({ labels, innerData, outerData }) => {
  const chartRef = useRef();
  const [activeTooltip, setActiveTooltip] = useState(null);

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    
    // Billions
    if (absValue >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    }
    // Millions
    if (absValue >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    // Thousands
    if (absValue >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    // Regular numbers
    return `$${value.toLocaleString()}`;
  };

  const data = useMemo(
    () => ({
      labels: labels,
      datasets: [
        // inner doughnut (Amount)
        {
          data: innerData,
          backgroundColor: backgroundColors.slice(0, innerData.length),
          radius: "70%",
          cutout: "0%",
          borderColor: "#fff",
          borderWidth: 2,
          borderRadius: 5,
        },
        // outer doughnut (Count)
        {
          data: outerData,
          offset: 10,
          backgroundColor: backgroundColors.slice(0, outerData.length),
          radius: "100%",
          cutout: "86%",
          borderColor: "#fff",
          borderWidth: 2,
          borderRadius: 5,
        },
      ],
    }),
    [labels, innerData, outerData],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          enabled: false,
          external: (context) => {
            const tooltipModel = context.tooltip;
            
            if (!tooltipModel || !tooltipModel.opacity || !tooltipModel.dataPoints?.length) {
              setActiveTooltip(null);
              return;
            }

            const dp = tooltipModel.dataPoints[0];
            const index = dp.dataIndex;
            const totalAmount = innerData.reduce((a, b) => a + b, 0);
            const totalCount = outerData.reduce((a, b) => a + b, 0);

            setActiveTooltip({
              label: labels[index],
              amount: innerData[index],
              amountPercent: Math.round((innerData[index] / totalAmount) * 100),
              count: outerData[index],
              countPercent: Math.round((outerData[index] / totalCount) * 100),
              x: tooltipModel.caretX,
              y: tooltipModel.caretY,
              color: backgroundColors[index],
            });
          },
        },
        legend: {
          display: false,
        },
      },
      layout: {
        padding: 20,
      },
      cutout: "50%",
    }),
    [labels, innerData, outerData],
  );

  return (
    <div style={{ position: "relative", width: 380, height: 320 }}>
      <Doughnut ref={chartRef} data={data} options={options} />
      {activeTooltip && (
        <div
          style={{
            position: "absolute",
            left: activeTooltip.x,
            top: activeTooltip.y,
            transform: "translate(-50%, -100%)",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            zIndex: 10,
            minWidth: "280px",
            fontFamily: "Work Sans, sans-serif",
          }}
        >
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            padding: "12px",
            borderBottom: "1px solid #E5E7EB"
          }}>
            <span style={{ 
              color: "#111827", 
              fontSize: "14px",
              fontWeight: "600",
              textTransform: "uppercase"
            }}>
              {activeTooltip.label}
            </span>
            <img 
              src={TOOLTIP_IMAGES[activeTooltip.label]} 
              alt={activeTooltip.label}
              style={{ height: "24px", width: "24px" }}
            />
          </div>
          <div style={{ padding: "12px" }}>
            <div style={{ 
              display: "flex", 
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: activeTooltip.color,
                }} />
                <span style={{ color: "#6B7280", fontSize: "14px" }}>Amount:</span>
              </div>
              <div style={{ 
                display: "flex", 
                alignItems: "center",
                gap: "12px"
              }}>
                <span style={{ color: "#6B7280", fontSize: "14px" }}>{activeTooltip.amountPercent}%</span>
                <span style={{ color: "#111827", fontSize: "14px", fontWeight: "600" }}>
                  {formatCurrency(activeTooltip.amount)}
                </span>
              </div>
            </div>
            <div style={{ 
              display: "flex", 
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: activeTooltip.color,
                }} />
                <span style={{ color: "#6B7280", fontSize: "14px" }}>Count</span>
              </div>
              <div style={{ 
                display: "flex", 
                alignItems: "center",
                gap: "12px"
              }}>
                <span style={{ color: "#6B7280", fontSize: "14px" }}>{activeTooltip.countPercent}%</span>
                <span style={{ color: "#111827", fontSize: "14px", fontWeight: "600" }}>
                  {activeTooltip.count.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoubleDonutChart;